import React from 'react';

import type { UUID } from '@aline/common';
import type { ExistingFieldRecipient } from 'src/components';
import {
  CheckboxVariable,
  DateVariable,
  NumberVariable,
  OptionsVariable,
  RadioOptionsVariable,
  TextVariable,
  useVariablePanelState,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import type { AnswerOptionsSubset, AnswerOptionSubset } from 'src/types';

type PanelVariableAction = ExistingFieldRecipient &
  Pick<Apollo.WorkflowAction, 'completedAt' | 'startedAt'>;
export interface PanelVariableType extends Pick<
  Apollo.DocumentVariable,
  | 'id'
  | 'isField'
  | 'addToLaunchForm'
  | 'label'
  | 'nodeId'
  | 'question'
  | '__typename'
  | 'value'
  | 'variant'
  | 'integrationLink'
  | 'minRequired'
  | 'maxRequired'
  | 'skipped'
  | 'dateFormat'
> {
  action: PanelVariableAction | null;
  answerOptions: AnswerOptionsSubset;
  defaultAnswer: AnswerOptionSubset | null;
  selectedAnswer: AnswerOptionSubset | null;
  selectedAnswers: AnswerOptionSubset[] | null;
}

interface VariableRowProps extends React.HTMLAttributes<HTMLElement> {
  documentId: UUID;
  variable: PanelVariableType;
}

export function VariableRow({
  className,
  documentId,
  variable,
}: VariableRowProps): JSX.Element | null {
  const state = useVariablePanelState();
  const isSelected = state.focusedField?.nodeId === variable.nodeId;

  switch (variable.variant) {
    case Apollo.MetadataVariant.Currency:
    case Apollo.MetadataVariant.Decimal:
      return (
        <NumberVariable
          className={className}
          documentId={documentId}
          isSelected={isSelected}
          numVariable={variable}
        />
      );
    case Apollo.MetadataVariant.Date:
      return (
        <DateVariable
          className={className}
          dateVariable={variable}
          documentId={documentId}
          isSelected={isSelected}
        />
      );
    case Apollo.MetadataVariant.Email:
    case Apollo.MetadataVariant.Text:
      return (
        <TextVariable
          className={className}
          documentId={documentId}
          isSelected={isSelected}
          textVariable={variable}
        />
      );
    case Apollo.MetadataVariant.Options:
      return (
        <OptionsVariable
          className={className}
          documentId={documentId}
          isSelected={isSelected}
          optsVariable={variable}
        />
      );
    case Apollo.MetadataVariant.RadioOptions:
      return (
        <RadioOptionsVariable
          className={className}
          documentId={documentId}
          isSelected={isSelected}
          optsVariable={variable}
        />
      );
    case Apollo.MetadataVariant.Checkbox:
      return (
        <CheckboxVariable
          checkboxVariable={variable}
          className={className}
          documentId={documentId}
          isSelected={isSelected}
        />
      );
  }
}
